import type { LiveScorecardMatch, LiveScorecardScore } from '../types/database';

export const TOURNAMENT_ABBREVIATIONS: Record<string, string> = {
  // IPL
  'indian premier league': 'IPL',
  // ICC men's — both API ("ICC Mens …") and CreateLeague form ("ICC …") variants
  'icc cricket world cup': 'ODI WC',
  'icc mens cricket world cup': 'ODI WC',
  'icc odi world cup': 'ODI WC',
  'icc mens t20 world cup': 'T20 WC',
  'icc t20 world cup': 'T20 WC',
  'icc mens champions trophy': 'Champions Trophy',
  'icc champions trophy': 'Champions Trophy',
  'icc world test championship': 'WTC',
  'icc mens cricket world cup super league': 'CWC Super League',
  // ICC women's
  'icc womens t20 world cup': "Women's T20 WC",
  'icc womens cricket world cup': "Women's ODI WC",
  'icc womens champions trophy': "Women's Champions Trophy",
  // Domestic T20 leagues
  'pakistan super league': 'PSL',
  'big bash league': 'BBL',
  'caribbean premier league': 'CPL',
  'sa20': 'SA20',
  'international league t20': 'ILT20',
  'major league cricket': 'MLC',
  'lanka premier league': 'LPL',
  'bangladesh premier league': 'BPL',
};

export function parseMatchMeta(name: string): { matchNumber: string | null; tournament: string | null } {
  const parts = name.split(',').map(s => s.trim());
  if (parts.length < 3) return { matchNumber: null, tournament: null };

  const matchNumber = parts[1] || null;
  const rawTournament = parts.slice(2).join(', ').trim();

  const yearMatch = rawTournament.match(/\b(20\d{2})\b/);
  const year = yearMatch ? ` ${yearMatch[1]}` : '';
  const nameOnly = rawTournament.replace(/\b20\d{2}\b/, '').trim().replace(/,?\s*$/, '');
  const key = nameOnly.toLowerCase();
  const short = TOURNAMENT_ABBREVIATIONS[key];
  const tournament = short ? `${short}${year}` : rawTournament;

  return { matchNumber, tournament };
}

export function formatScoreLine(score: LiveScorecardScore): string {
  const overs = Number.isInteger(score.o) ? `${score.o}` : `${score.o.toFixed(1)}`;
  return `${score.r}/${score.w} (${overs})`;
}

export type StatusKind = 'live' | 'completed' | 'upcoming' | 'cancelled';

export function getStatusKind(match: LiveScorecardMatch): StatusKind {
  const status = (match.status || '').toLowerCase();
  if (
    status.includes('cancel') ||
    status.includes('abandon') ||
    status.includes('no result')
  ) {
    return 'cancelled';
  }
  if (match.matchEnded) return 'completed';
  if (match.matchStarted) return 'live';
  return 'upcoming';
}

export function findTeamScore(scores: LiveScorecardScore[], teamName: string): LiveScorecardScore | null {
  const lower = (teamName || '').toLowerCase();
  return (
    scores.find((s) => (s.inning || '').toLowerCase().includes(lower)) || null
  );
}

function utcDayKey(d: Date): string {
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}-${String(d.getUTCDate()).padStart(2, '0')}`;
}

/**
 * Returns a year-stripped, abbreviation-normalized key for tournament matching.
 * "IPL 2026" → "ipl", "Indian Premier League 2026" → "ipl",
 * "ICC T20 World Cup" → "t20 wc", "IPL" → "ipl".
 */
export function tournamentBaseKey(name: string): string {
  if (!name) return '';
  const nameOnly = name.replace(/\b20\d{2}\b/, '').trim().replace(/,?\s*$/, '');
  const key = nameOnly.toLowerCase();
  const short = TOURNAMENT_ABBREVIATIONS[key];
  return (short ?? nameOnly).toLowerCase().trim();
}

/**
 * Select matches for display:
 * - Filtered to tournaments the user participates in (when allowedTournamentNames provided)
 * - All live matches (regardless of date)
 * - All completed matches from today (UTC)
 * - All upcoming matches from today and tomorrow (UTC)
 * Sorted: live → completed → upcoming, chronologically within each tier.
 */
export function selectForDisplay(
  all: LiveScorecardMatch[],
  allowedTournamentNames?: string[],
  now: Date = new Date()
): LiveScorecardMatch[] {
  if (all.length === 0) return [];

  const allowedKeys = allowedTournamentNames && allowedTournamentNames.length > 0
    ? new Set(allowedTournamentNames.map(tournamentBaseKey))
    : null;

  const todayKey = utcDayKey(now);
  const tomorrowKey = utcDayKey(new Date(now.getTime() + 86_400_000));

  const relevant = all.filter((m) => {
    if (allowedKeys) {
      const { tournament } = parseMatchMeta(m.name);
      if (!tournament || !allowedKeys.has(tournamentBaseKey(tournament))) return false;
    }
    if (m.matchStarted && !m.matchEnded) return true;
    const dateKey = m.dateTimeGMT ? utcDayKey(new Date(m.dateTimeGMT)) : null;
    return dateKey === todayKey || dateKey === tomorrowKey;
  });

  return relevant.sort((a, b) => {
    const tier = (m: LiveScorecardMatch): number => {
      if (m.matchStarted && !m.matchEnded) return 0;
      if (m.matchEnded) return 1;
      return 2;
    };
    const ta = tier(a);
    const tb = tier(b);
    if (ta !== tb) return ta - tb;
    const da = a.dateTimeGMT ? new Date(a.dateTimeGMT).getTime() : 0;
    const db = b.dateTimeGMT ? new Date(b.dateTimeGMT).getTime() : 0;
    return da - db;
  });
}
